/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file hook to toggle local video track background processor enable/disable
 * @author Liam Liden
 * @module Epic.VideoApp.Hooks.BackgroundEffects.UseBackgroundToggle
 */

import { useDispatch } from "@epic/react-redux-booster";
import { useCallback, useContext } from "react";
import { backgroundProcessorsActions, useBackgroundProcessorsState } from "~/state/backgroundProcessors";
import { ToggleState } from "~/types";
import { DefaultImageNames } from "~/types/backgrounds";
import { boolMatchesToggleState } from "~/utils/general";
import { VideoContext } from "~/web-core/components";
import { ILocalStream } from "~/web-core/interfaces";

interface IBackgroundToggle {
	isEnabled: boolean;
	toggleProcessor: (forceState?: ToggleState) => void;
}

/**
 * Hook to get the status of if the local video track background processor is enabled and toggle enable/disable
 * @param path The path of the image used for the background processor and the key used to locate the background processor in shared state map
 * @param previewStream Preview stream that the background processor should be applied to; if not given processor is applied to local device stream
 * @returns tuple with the current enabled/disabled status and function to toggle that status
 */
export function useBackgroundToggle(path: string, previewStream?: ILocalStream): IBackgroundToggle {
	const { session } = useContext(VideoContext);
	const localUser = session?.localUser;

	const dispatch = useDispatch();

	const publishedProcessor = useBackgroundProcessorsState(
		(selectors) => selectors.getPublishedBackgroundProcessor(),
		[],
	);
	const previewProcessor = useBackgroundProcessorsState(
		(selectors) => selectors.getPreviewBackgroundProcessor(),
		[],
	);
	const targetProcessor = previewStream ? previewProcessor : publishedProcessor;
	const processorMap = useBackgroundProcessorsState((selectors) => selectors.getBackgroundProcessors(), []);

	// Check if processor type is already set on the published track
	const isEnabled = path === targetProcessor;

	// Callback to toggle the processor on/off
	const toggleProcessor = useCallback(
		async (forceState?: ToggleState) => {
			if (!localUser) {
				return;
			}
			if (boolMatchesToggleState(isEnabled, forceState)) {
				return;
			}

			const newProcessorPath = isEnabled ? DefaultImageNames.none : path;
			const processor = processorMap.get(newProcessorPath)?.processor ?? null;
			await localUser.applyVideoBackground(processor, previewStream ?? null);
			if (previewStream) {
				dispatch(backgroundProcessorsActions.setPreviewBackgroundProcessor(newProcessorPath));
			} else {
				dispatch(backgroundProcessorsActions.setPublishedBackgroundProcessor(newProcessorPath));
			}
		},
		[dispatch, isEnabled, localUser, path, previewStream, processorMap],
	);

	return { isEnabled, toggleProcessor };
}
